import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TablaMaterialesVista from "../vista/tablaMaterialesVista";
import type { IMaterial } from "../../../modelos/Materiales/entidades/IMaterial";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasTablaMaterial } from "../vista/constantesTablaMateriales";
import { estadoInicialMaterial } from "../interfaces";
import { MaterialModelo } from "../../../modelos/Materiales";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion, type IFiltroPaginador } from "../../../globales/interfaces";
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { type ITipoMaterial } from "../../../modelos/Materiales/entidades/ITipoMaterial";

const MaterialesCV = () => {
  const modeloMaterial = new MaterialModelo();

  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();

  const TAMANIO_PAGINA = 15;
  const [indicePagina, setIndicePagina] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [totalMateriales, setTotalMateriales] = useState(0);

  const [listaMateriales, setListaMateriales] = useState<IMaterial[]>([]);
  const [tiposMateriales, setTiposMateriales] = useState<ITipoMaterial[]>([]);
  const [habilitarFiltros, setHabilitarFiltros] = useState<boolean>(false);
  const [filtroMaterial, setFiltroMaterial] = useState<IMaterial>(estadoInicialMaterial);

  // Estados para edición en línea
  const [modoEdicionLinea] = useState(true);
  const [filaEnEdicion, setFilaEnEdicion] = useState<number | null>(null); // ✅ Cambiar a number
  const [datosEdicion, setDatosEdicion] = useState<IMaterial | null>(null); // ✅ Simplificado
  const [contadorTemporal, setContadorTemporal] = useState(-1);

  useEffect(() => {
    // consultarMateriales(estadoInicialMaterial, { Page: 1, Size: TAMANIO_PAGINA });
    consultarTiposMateriales();

    return () => {
      if (filaEnEdicion !== null && datosEdicion && datosEdicion.Id < 0) {
        setListaMateriales(prev => prev.filter(m => m.Id !== datosEdicion.Id));
      }
    };
  }, []);

  const datosEdicionRef = useRef<IMaterial | null>(null);

  // useEffect(() => {
  //   datosEdicionRef.current = datosEdicion;
  // }, [datosEdicion]);


  // 👆 Detectar clics fuera de la tabla cuando hay edición activa
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Solo actuar si hay una fila en edición y es nueva (ID negativo)
      if (filaEnEdicion !== null && datosEdicion && datosEdicion.Id < 0) {
        const target = event.target as HTMLElement;

        // Verificar si el clic fue fuera de la tabla
        const tablaContainer = target.closest('[data-tabla-materiales]');

        // Verificar si el clic fue en un dropdown/modal/popover/input de Mantine
        const mantinePortal = target.closest('[data-portal]') ||
          target.closest('.mantine-Select-dropdown') ||
          target.closest('.mantine-Modal-root') ||
          target.closest('.mantine-Popover-dropdown') ||
          target.closest('.mantine-Menu-dropdown') ||
          target.closest('.mantine-TextInput-root') ||
          target.closest('.mantine-NumberInput-root') ||
          target.closest('.mantine-Switch-root') ||
          target.closest('input') ||
          target.closest('button') ||
          target.closest('tr') ||  // Detectar clic en la fila
          target.closest('td') ||  // Detectar clic en las celdas
          target.tagName === 'INPUT' ||
          target.tagName === 'BUTTON' ||
          target.tagName === 'TR' ||  // Verificar si es una fila
          target.tagName === 'TD';    // Verificar si es una celda

        // Solo cancelar si el clic fue fuera de la tabla Y fuera de componentes de Mantine
        if (!tablaContainer && !mantinePortal) {
          cancelarEdicion();
        }
      }
    };

    // Agregar el listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filaEnEdicion, datosEdicion]);

  const consultarMateriales = async (filtro: IMaterial, paginador: IFiltroPaginador) => {
    try {
      manejarCargando(true, "Consultando materiales...", false);

      const materiales = await modeloMaterial.obtenerListaMateriales(filtro, paginador);

      // Filtramos los no eliminados

      // Finalmente, seteamos los materiales filtrados
      setTotalMateriales(materiales.Pagination.Total ?? materiales.Data.length);
      setListaMateriales(materiales.Data);
    } catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar materiales',
        'No fue posible consultar los materiales. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  };


  const consultarTiposMateriales = async () => {
    try {
      manejarCargando(true, undefined, false);
      const tiposMateriales = await modeloMaterial.obtenerListaTipoMaterial();
      setTiposMateriales(tiposMateriales.Data);
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar los tipos de materiales',
        'No fue posible consultar los tipos de materiales. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    }
    finally {
      manejarCargando(false);
    }
  }

  const limpiarFiltros = useCallback(() => {
    setFiltroMaterial(estadoInicialMaterial);
    consultarMateriales(estadoInicialMaterial, { Page: 1, Size: TAMANIO_PAGINA });
  }, []);

  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroMaterial = async (campo: keyof IMaterial, valor: IMaterial[keyof IMaterial]) => {
    setFiltroMaterial((prevFiltroMaterial) => ({
      ...prevFiltroMaterial,
      [campo]: valor
    }));
  }

  const eliminarMaterial = async (material: IMaterial) => {
    const confirmado = await confirmar({
      titulo: "Eliminar material",
      mensaje: "¿Está seguro de que desea eliminar este material? Esta acción no se puede deshacer.",
      textoAceptar: "Sí, eliminar",
      textoCancelar: "Cancelar",
      tipo: EnumTipoNotificacion.Error,
      opcionCancelar: true
    });
    if (confirmado) {
      try {
        manejarCargando(true);
        const materialRespuesta = await modeloMaterial.eliminarMaterial(material);
        if (materialRespuesta.NotificationType === EnumTipoNotificacion.Success) {
          consultarMateriales(estadoInicialMaterial, { Page: 1, Size: TAMANIO_PAGINA });
        }
        if (materialRespuesta.Messages.length > 0) {
          materialRespuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              materialRespuesta.NotificationType,
              '',
              mensaje,
              4000
            ));
        }
      }
      catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error al eliminar material',
          'No fue posible eliminar el material. Por favor verifica tu conexión e intenta nuevamente.',
          5000
        );
      } finally {
        manejarCargando(false);
      }
    }
  }

  // ✅ Iniciar edición de una fila existente
  const iniciarEdicion = (material: IMaterial) => {
    if (modoEdicionLinea) {
      setFilaEnEdicion(material.Id);
      setDatosEdicion({ ...material });
    } else {
    }
  }

  // ✅ Agregar nueva fila para edición
  const agregarNuevoMaterial = () => {
    if (modoEdicionLinea) {
      const nuevoId = contadorTemporal;

      const nuevoMaterial: IMaterial = {
        ...estadoInicialMaterial,
        Id: nuevoId,
        Activo: true,
      };

      // Agregar al inicio de la lista
      setListaMateriales([nuevoMaterial, ...listaMateriales]);
      setFilaEnEdicion(nuevoId);
      setDatosEdicion(nuevoMaterial);
      setContadorTemporal(contadorTemporal - 1);

      // Ir a la primera página para ver el nuevo registro
      setIndicePagina(0);
    } else {
    }
  }

  // ✅ Manejar cambios en los campos durante edición
  const manejarCambioValor = useCallback((
    materialId: number,
    campo: keyof IMaterial,
    valor: IMaterial[keyof IMaterial]
  ) => {
    if (filaEnEdicion !== materialId) return;

    setDatosEdicion(prev => {
      if (!prev) return null;
      const actualizado = { ...prev, [campo]: valor };
      datosEdicionRef.current = actualizado;
      return actualizado;
    });

  }, [filaEnEdicion]);


  const validarCamposMaterial = () => {
    let esValido = true;
    const datos = datosEdicionRef.current;

    if (!datos?.Sku.trim()) {
      esValido = false;
    }

    if (!datos?.Nombre.trim()) {
      esValido = false;
    }

    if (!datos?.TipoMaterialId || datos.TipoMaterialId === 0) {
      esValido = false;
    }

    if (!esValido) {
      mostrarNotificacion(
        EnumTipoNotificacion.Warning,
        "Campos requeridos (*)",
        "Por favor completa todos los campos antes de continuar.",
        3000
      );
    }

    return esValido;
  };

  // ✅ Guardar edición
  const guardarEdicion = async (materialId: number) => {
    const datos = datosEdicionRef.current; // 🔹 lee el valor actual en tiempo real
    if (!datos || filaEnEdicion !== materialId) return;

    if (!validarCamposMaterial()) return;

    try {
      manejarCargando(true);
      let respuesta;
      const esNuevo = datos.Id < 0;

      if (esNuevo) {
        respuesta = await modeloMaterial.crearMaterial({ ...datos, Id: 0 });
      } else {
        respuesta = await modeloMaterial.editarMaterial(datos);
      }

      if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
        mostrarNotificacion(
          EnumTipoNotificacion.Success,
          "",
          esNuevo
            ? "Material creado exitosamente"
            : "Material actualizado exitosamente",
          3000
        );

        cancelarEdicion();
        await consultarMateriales(estadoInicialMaterial, { Page: 1, Size: TAMANIO_PAGINA });
      } else {
        if (respuesta.Messages.length > 0) {
          respuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              respuesta.NotificationType,
              "",
              mensaje,
              4000
            )
          );
        }
      }
    } catch (error) {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        "Error",
        "No fue posible guardar el material. Por favor verifica tu conexión e intenta nuevamente.",
        5000
      );
    } finally {
      manejarCargando(false);
    }
  };


  // ✅ Cancelar edición
  const cancelarEdicion = () => {
    if (filaEnEdicion !== null && datosEdicion) {
      // Si era un material nuevo (ID negativo), quitarlo de la lista
      if (datosEdicion.Id < 0) {
        setListaMateriales(listaMateriales.filter(m => m.Id !== datosEdicion.Id));
      }
    }
    setFilaEnEdicion(null);
    setDatosEdicion(null);
  }

  // ✅ Actualizar listaMateriales con datos de edición
  const materialesConEdicion = listaMateriales.map(material => {
    if (material.Id === filaEnEdicion && datosEdicion) {
      return datosEdicion;
    }
    return material;
  });

  const columns = useMemo(() => columnasTablaMaterial({
    manejarEdicionMaterial: iniciarEdicion,
    manejarEliminacionMaterial: eliminarMaterial,
    modoEdicionLinea,
    filaEnEdicion,
    onCambioValor: manejarCambioValor,
    onGuardarEdicion: guardarEdicion,
    onCancelarEdicion: cancelarEdicion,
    tiposMateriales,
  }), [
    modoEdicionLinea,
    filaEnEdicion,
    tiposMateriales,
  ]);


  const tabla = useReactTable({
    data: materialesConEdicion,
    columns: columns, // ✅ Aquí directamente
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    manualPagination: true,
    pageCount: Math.ceil(totalMateriales / TAMANIO_PAGINA),
    state: { pagination: { pageIndex: indicePagina, pageSize: TAMANIO_PAGINA }, sorting},
    onPaginationChange: updater => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex: indicePagina, pageSize: TAMANIO_PAGINA });
        setIndicePagina(newState.pageIndex);
        // 💡 Sumamos +1 al enviar al backend
        consultarMateriales(filtroMaterial, { Page: newState.pageIndex + 1, Size: newState.pageSize });
      } else {
        setIndicePagina(updater.pageIndex ?? 0);
        // 💡 Sumamos +1 al enviar al backend
        consultarMateriales(filtroMaterial, { Page: (updater.pageIndex ?? 0) + 1, Size: TAMANIO_PAGINA });
      }
    },
    getRowId: (row) => row.Id.toString(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  const descargarPlantilla = async () => {
    try {
      manejarCargando(true, undefined, true);

      await modeloMaterial.descargarPlantilla();

      mostrarNotificacion(
        EnumTipoNotificacion.Success,
        "Descarga completada",
        "La plantilla se descargó correctamente.",
        4000
      );
    } catch (error) {
      console.error("Error al descargar la plantilla:", error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        "Error al descargar",
        "No fue posible descargar la plantilla. Por favor verifica tu conexión e intenta nuevamente.",
        5000
      );
    } finally {
      manejarCargando(false);
    }
  };


  const cargaMasivaMateriales = async () => {
    try {
      // Crear input de archivo
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx";

      input.onchange = async (e: any) => {
        const archivo = e.target.files[0];
        if (!archivo) return;

        try {
          manejarCargando(true, "Cargando archivo...", true);

          const respuesta = await modeloMaterial.cargarMasiva(archivo);

          if (respuesta?.Messages?.length > 0) {
            respuesta.Messages.forEach(mensaje =>
              mostrarNotificacion(
                respuesta.NotificationType ?? EnumTipoNotificacion.Information,
                "",
                mensaje,
                4000
              )
            );
          } else {
            mostrarNotificacion(
              EnumTipoNotificacion.Success,
              "Carga masiva completada",
              "El archivo fue cargado correctamente.",
              4000
            );
          }
        } catch (error) {
          console.error("Error durante la carga masiva:", error);
          mostrarNotificacion(
            EnumTipoNotificacion.Error,
            "Error al cargar",
            "No fue posible procesar el archivo. Verifica el formato o la conexión.",
            5000
          );
        } finally {
          manejarCargando(false);
        }
      };

      // Disparar selector
      input.click();
    } catch (error) {
      console.error("Error inicial en carga masiva:", error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        "Error inesperado",
        "Ocurrió un error al intentar seleccionar el archivo.",
        5000
      );
    }
  };



  return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TablaMaterialesVista
          abrirModalMaterial={agregarNuevoMaterial}
          tabla={tabla}
          consultarMateriales={() => consultarMateriales(filtroMaterial, { Page: 1, Size: TAMANIO_PAGINA })}
          filtro={filtroMaterial}
          habilitarFiltros={habilitarFiltros}
          manejarCambioFiltroMaterial={manejarCambioFiltroMaterial}
          manejarHabilitarFiltros={manejarHabilitarFiltros}
          modoEdicionLinea={modoEdicionLinea}
          filaEnEdicion={filaEnEdicion}
          tiposMateriales={tiposMateriales}
          descargarPlantilla={descargarPlantilla}
          cargaMasivaMateriales={cargaMasivaMateriales}
          limpiarFiltros={limpiarFiltros}
        />
      </div>
  )
}

export default MaterialesCV;